import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MigrationProgress } from "@/lib/migrations/populateCodeIds";

interface CodeIdMigrationSectionProps {
  migrationCount: number;
  isMigrating: boolean;
  migrationProgress: MigrationProgress | null;
  onMigrate: () => void;
}

export function CodeIdMigrationSection({
  migrationCount,
  isMigrating,
  migrationProgress,
  onMigrate
}: CodeIdMigrationSectionProps) {
  return (
    <div className="border border-border rounded p-6 space-y-4 bg-card">
      <div className="flex items-start gap-4">
        <IconProvider iconstring="fa-wrench" size={12} className="text-primary" />
        <div className="flex-1">
          <h2 className="heading-4">Code ID Migration</h2>
          <p className="text-muted-foreground mb-4">
            Generate unique code identifiers for library objects that don't have one yet. 
            Code IDs are used in exports to reference objects from code (e.g., "characters/npcs_hero").
          </p>
          
          {migrationCount > 0 ? (
            <>
              <Alert className="mb-4">
                <IconProvider iconstring="fa-info-circle" size={14} className="mr-2" />
                <AlertDescription>
                  <strong>{migrationCount} object{migrationCount !== 1 ? 's' : ''}</strong> need{migrationCount === 1 ? 's' : ''} code IDs.
                  {migrationProgress && (
                    <div className="mt-2 space-y-1">
                      <div className="text-sm">
                        Progress: {migrationProgress.processed} / {migrationProgress.total}
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${(migrationProgress.processed / migrationProgress.total) * 100}%` }}
                        />
                      </div>
                      {migrationProgress.errors.length > 0 && (
                        <div className="text-sm text-destructive mt-2">
                          {migrationProgress.errors.length} error{migrationProgress.errors.length !== 1 ? 's' : ''} occurred
                        </div>
                      )}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
              <Button
                onClick={onMigrate}
                disabled={isMigrating}
                size="lg"
                className="gap-2"
              >
                {isMigrating ? (
                  <>
                    <IconProvider iconstring="fa-spinner" className="animate-spin" />
                    Generating Code IDs...
                  </>
                ) : (
                  <>
                    <IconProvider iconstring="fa-magic" size={16} />
                    Generate Code IDs
                  </>
                )}
              </Button>
            </>
          ) : (
            <Alert>
              <IconProvider iconstring="fa-check-circle" size={14} className="mr-2 text-green-500" />
              <AlertDescription>
                All library objects have code IDs assigned.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}
